import { useEffect, useRef } from 'react';
import { useLocation, useNavigation, useParams, matchPath } from 'react-router-dom';
import { angularInjectorProvider } from 'Injector';
import { getRouteRegistry } from 'FrontRoyalRouteRegistry';
import { type ReactRouterBridge } from 'Navigation/angularModule/scripts/react_router_bridge';
import { cleanUpRouteResolution } from 'FrontRoyalAngular';
import { useSelector } from 'react-redux';
import { selectRouteTransition, type Phase } from 'RouteAnimation';
import { useAllowBlockNavigation } from './useAllowBlockNavigation';

export function useSetupReactRouterBridge(): void {
    const location = useLocation();
    const params = useParams();
    const navigation = useNavigation();
    const { phase: routeAnimationPhase } = useSelector(selectRouteTransition);

    // Track previous values to detect changes during render
    // Initialize to undefined so first render is treated as a change
    const prevLocationRef = useRef<ReturnType<typeof useLocation> | undefined>(undefined);
    const prevParamsRef = useRef<Readonly<Record<string, string | undefined>> | undefined>(undefined);
    const prevAnimationPhaseRef = useRef<Phase | undefined>(undefined);

    // CRITICAL: Update bridge synchronously during render (before commit phase)
    // This ensures Angular directives see the updated bridge state when they mount
    const injector = angularInjectorProvider.injector;
    if (injector) {
        // Check if location/params changed since last render
        const locationChanged = prevLocationRef.current !== location;
        const paramsChanged = prevParamsRef.current !== params;
        const animationPhaseChanged = prevAnimationPhaseRef.current !== routeAnimationPhase;

        if (locationChanged || paramsChanged) {
            updateBridgeStateSync(injector, location, params);
            prevLocationRef.current = location;
            prevParamsRef.current = params;
        }

        if (animationPhaseChanged) {
            updateRouteAnimationPhaseSync(injector, routeAnimationPhase);
            prevAnimationPhaseRef.current = routeAnimationPhase;
        }
    }

    // Keep effects for post-commit cleanup and secondary updates
    useEffect(() => {
        handleNavigationState(navigation.state);
    }, [navigation.state]);

    useAllowBlockNavigation(location);
}

export const setupReactRouterBridge = useSetupReactRouterBridge;

/**
 * Synchronously updates the bridge state during render (before commit).
 * This ensures Angular directives see the updated state when they initialize.
 *
 * IMPORTANT: Called during render, so it must be idempotent and side-effect free
 * (the bridge update itself is the intended side effect, similar to useSyncExternalStore).
 */
function updateBridgeStateSync(
    injector: angular.auto.IInjectorService,
    location: ReturnType<typeof useLocation>,
    params: Readonly<Record<string, string | undefined>>,
): void {
    const bridge = injector.get<ReactRouterBridge>('reactRouterBridge');
    const { pathname, search, hash } = location;

    const searchParamsMap = new URLSearchParams(search);
    const searchParams: Record<string, string> = {};
    searchParamsMap.forEach((value, key) => {
        searchParams[key] = value;
    });

    const paramsRecord: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (typeof value === 'string') {
            paramsRecord[key] = value;
        }
    });

    const routeName = getRouteNameFromPath(pathname);

    // Update bridge synchronously - no Angular digest needed during render
    bridge.updateFromReact({
        pathname,
        searchParams: Object.keys(searchParams).length ? searchParams : {},
        hashString: hash,
        hostName: typeof window !== 'undefined' ? window.location.hostname : null,
        portValue: typeof window !== 'undefined' ? window.location.port : null,
        fullUrl: `${pathname}${search}${hash}`,
        paramsRecord: Object.keys(paramsRecord).length ? paramsRecord : {},
        routeName,
    });
}

function handleNavigationState(state: ReturnType<typeof useNavigation>['state']): void {
    const injector = angularInjectorProvider.injector;
    if (!injector) return;

    if (state === 'idle') {
        cleanUpRouteResolution(injector);
    }
}

/**
 * Synchronously updates the route animation phase during render.
 */
function updateRouteAnimationPhaseSync(injector: angular.auto.IInjectorService, routeAnimationPhase: Phase): void {
    const bridge = injector.get<ReactRouterBridge>('reactRouterBridge');
    if (bridge.routeAnimationPhase === routeAnimationPhase) return;

    bridge.updateFromReact({ routeAnimationPhase });
}

function getRouteNameFromPath(pathname: string): string | null {
    const matchedPattern = findMatchingRoutePattern(pathname);
    if (!matchedPattern) {
        return null;
    }

    return generateRouteNameFromPattern(matchedPattern);
}

function findMatchingRoutePattern(pathname: string): string | null {
    const routeRegistry = getRouteRegistry();

    if (routeRegistry.has(pathname)) {
        return pathname;
    }

    return (
        Array.from(routeRegistry.keys()).find(pattern => {
            const normalizedPattern = normalizePattern(pattern);
            return matchPath(normalizedPattern, pathname);
        }) ?? null
    );
}

function normalizePattern(pattern: string): string {
    const prefixed = pattern.startsWith('/') ? pattern : `/${pattern}`;
    return prefixed.replace(/:(\w+)\?/g, ':$1');
}

function generateRouteNameFromPattern(pattern: string): string {
    const normalized = pattern.startsWith('/') ? pattern.slice(1) : pattern;
    const segments = normalized.split('/');
    const firstParamIndex = segments.findIndex(segment => segment.startsWith(':'));
    const baseSegments = firstParamIndex === -1 ? segments : segments.slice(0, firstParamIndex);

    if (baseSegments.length === 0) {
        return 'root';
    }

    return baseSegments.join('-');
}
